<template>
  <img
    v-if="!showFallback"
    :src="currentSrc ?? undefined"
    :alt="alt"
    :class="className"
    :style="mergedStyle"
    @error="handleImageError"
    v-bind="passthroughAttrs"
  />
  <AvatarFallback
    v-else
    :name="alt"
    :size="fallbackSize"
    :class="className"
    :style="mergedStyle"
    v-bind="passthroughAttrs"
  />
</template>

<script setup lang="ts">
import { computed, ref, useAttrs, watch, type StyleValue } from 'vue'
import AvatarFallback from '@/components/ui/AvatarFallback.vue'
import { imageFocusToObjectPosition, type ImageFocus } from '@/lib/media/imageAssetFields'

defineOptions({ inheritAttrs: false })

type FallbackSize = 'sm' | 'md' | 'lg'

const props = withDefaults(defineProps<{
  src?: string | null
  alt?: string
  fallbackSize?: FallbackSize
  className?: string
  imageFocus?: ImageFocus | null
}>(), {
  src: null,
  alt: '',
  fallbackSize: 'md',
  className: '',
  imageFocus: null,
})

const attrs = useAttrs()
const currentSrc = ref<string | null>(props.src)
const showFallback = ref(!props.src)

const passthroughAttrs = computed(() => {
  const { style: _style, ...rest } = attrs
  return rest
})
const mergedStyle = computed<StyleValue>(() => [
  attrs.style as StyleValue,
  props.imageFocus ? { objectPosition: imageFocusToObjectPosition(props.imageFocus) } : null,
])

const handleImageError = () => {
  showFallback.value = true
}

watch(
  () => props.src,
  (newSrc) => {
    if (newSrc) {
      showFallback.value = false
      currentSrc.value = newSrc
    } else {
      showFallback.value = true
    }
  },
)
</script>
